import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import { ArrowRight, FileText, Languages } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

type Sample = {
  value: "policy" | "technical" | "marketing"
  labelKey: string
  sourceLanguageKey: string
  targetLanguageKey: string
  sourceTextKey: string
  translatedTextKey: string
}

const SAMPLES: ReadonlyArray<Sample> = [
  {
    value: "policy",
    labelKey: "sample_policy_label",
    sourceLanguageKey: "sample_policy_source_lang",
    targetLanguageKey: "sample_policy_target_lang",
    sourceTextKey: "sample_policy_source",
    translatedTextKey: "sample_policy_translated",
  },
  {
    value: "technical",
    labelKey: "sample_technical_label",
    sourceLanguageKey: "sample_technical_source_lang",
    targetLanguageKey: "sample_technical_target_lang",
    sourceTextKey: "sample_technical_source",
    translatedTextKey: "sample_technical_translated",
  },
  {
    value: "marketing",
    labelKey: "sample_marketing_label",
    sourceLanguageKey: "sample_marketing_source_lang",
    targetLanguageKey: "sample_marketing_target_lang",
    sourceTextKey: "sample_marketing_source",
    translatedTextKey: "sample_marketing_translated",
  },
] as const

export function DemoTranslatorBeforeAfter() {
  const { t } = useTranslation("", { keyPrefix: "training.ai-translator.use_cases" })
  const [active, setActive] = useState<Sample["value"]>("policy")

  const activeSample = useMemo(
    () => SAMPLES.find((s) => s.value === active) ?? SAMPLES[0],
    [active]
  )

  return (
    <div className="w-full">
      <Tabs
        className="w-full"
        value={active}
        onValueChange={(v) => setActive(v as Sample["value"])}
      >
        <TabsList className="w-full flex-wrap justify-start gap-1">
          {SAMPLES.map((s) => (
            <TabsTrigger key={s.value} value={s.value}>
              <FileText className="size-4" />
              <span>{t(s.labelKey)}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {SAMPLES.map((s) => (
          <TabsContent key={s.value} value={s.value}>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="bg-muted/30 flex flex-col gap-2 rounded-xl border p-4">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground text-[10px] font-bold tracking-wide uppercase">
                    {t("before_label", "Source")}
                  </span>
                  <Badge variant="secondary">{t(s.sourceLanguageKey)}</Badge>
                </div>
                <p className="text-sm leading-relaxed">{t(s.sourceTextKey)}</p>
              </div>

              <div className="bg-primary/5 border-primary/30 flex flex-col gap-2 rounded-xl border p-4">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-primary flex items-center gap-1.5 text-[10px] font-bold tracking-wide uppercase">
                    <Languages className="size-3.5" />
                    {t("after_label", "Translation")}
                  </span>
                  <Badge className="bg-primary/15 text-primary border-primary/30">
                    {t(s.targetLanguageKey)}
                  </Badge>
                </div>
                <p className="text-sm leading-relaxed">{t(s.translatedTextKey)}</p>
              </div>
            </div>

            <div className="text-muted-foreground mt-3 flex items-center justify-center gap-2 text-xs md:hidden">
              <ArrowRight className="size-3.5" />
              <span>
                {t(activeSample.sourceLanguageKey)} → {t(activeSample.targetLanguageKey)}
              </span>
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}
